class AppointmentPicker extends HTMLElement {
  constructor() {
    super();
    this.apiBase = this.dataset.apiBase || '';
    this.locationId = this.dataset.locationId || '';
    this.formId = this.dataset.formId || '';

    // State
    this.purchaseState = 1;
    this.forWhom = 'self'; // 'self', 'other', 'gift'
    this.isGift = false;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.holdToken = null;
    this.holdAppointmentId = null;
    this.holdExpiresAt = null;
    this.holdTimer = null;
    this.deferred = false;
    this.availableDates = new Set();
    this.slots = [];
    this.currentMonth = null;
    this.recipientName = '';
    this.recipientEmail = '';
    this.giftMessage = '';
    this.giftRevealDate = '';
    this.giftRevealTiming = 'now'; // 'now', 'later'
    this.deferredAppointmentId = null;

    // Update mode state
    this.updateMode = false;
    this.updateAppointmentId = null;
    this.updateLineItemKey = null;
  }

  connectedCallback() {
    const now = new Date();
    this.currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const params = new URLSearchParams(window.location.search);
    const appointmentId = params.get('appointment');
    if (appointmentId) {
      this.loadUpdateMode(appointmentId);
    } else {
      this.render();
    }
  }

  disconnectedCallback() {
    if (this.holdTimer) clearInterval(this.holdTimer);
    if (this._btnObserver) this._btnObserver.disconnect();
  }

  // --- Derived state ---

  get effectiveForWhom() {
    if (this.forWhom === 'other' && this.isGift) return 'gift';
    return this.forWhom;
  }

  get hasValidRecipient() {
    if (!this.recipientName.trim()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.recipientEmail.trim());
  }

  get isComplete() {
    const whom = this.effectiveForWhom;
    // Gifts are always deferred
    if (whom === 'gift') {
      if (!this.hasValidRecipient) return false;
      if (this.giftRevealTiming === 'later' && !this.giftRevealDate) return false;
      return true;
    }
    if (this.deferred) {
      if (whom !== 'self' && !this.hasValidRecipient) return false;
      return true;
    }
    if (!this.selectedSlot || !this.holdToken) return false;
    if (whom === 'other') {
      if (!this.hasValidRecipient) return false;
    }
    return true;
  }

  // What's actually missing, for the button hint + which section to highlight.
  get completionHint() {
    const whom = this.effectiveForWhom;
    if ((whom === 'other' || whom === 'gift') && !this.hasValidRecipient) {
      return "Add the recipient's details to continue";
    }
    if (whom === 'gift' && this.giftRevealTiming === 'later' && !this.giftRevealDate) {
      return 'Choose when to reveal the gift to continue';
    }
    if (whom !== 'gift' && !this.deferred && (!this.selectedSlot || !this.holdToken)) {
      return 'Choose a measurement appointment option to continue';
    }
    return '';
  }

  incompleteSection() {
    const whom = this.effectiveForWhom;
    if ((whom === 'other' || whom === 'gift') && !this.hasValidRecipient) {
      return this.querySelector('.appointment-picker__other-section');
    }
    if (whom !== 'gift' && !this.deferred && (!this.selectedSlot || !this.holdToken)) {
      return this.querySelector(`.appointment-picker__schedule-section[data-whom="${whom}"]`);
    }
    return null;
  }

  computePurchaseState() {
    const whom = this.effectiveForWhom;
    if (whom === 'self') {
      return this.deferred ? 2 : 1;
    }
    if (whom === 'other') {
      return this.deferred ? 4 : 3;
    }
    if (whom === 'gift') {
      return this.giftRevealDate ? 6 : 5;
    }
    return 1;
  }

  // --- API ---

  async loadAvailableDates() {
    if (!this.locationId) return;
    const url = `${this.apiBase}/available-dates/?location=${this.locationId}&month=${this.currentMonth}`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      this.availableDates = new Set(data.available_dates || []);
    } catch {
      this.showModalError('Could not load availability.');
    }
    this.renderCalendar();
  }

  async loadSlots(dateStr) {
    const url = `${this.apiBase}/available-slots/?location=${this.locationId}&date=${dateStr}`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      this.slots = data.slots || [];
    } catch {
      this.showModalError('Could not load time slots.');
    }
    this.renderSlots();
  }

  async holdSlot(slotId) {
    await this.releaseHold();
    const url = `${this.apiBase}/hold/`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot_id: slotId }),
      });
      if (!res.ok) {
        const data = await res.json();
        this.showModalError(data.error || 'Slot unavailable.');
        this.selectedSlot = null;
        this.renderSlots();
        return false;
      }
      const data = await res.json();
      this.holdToken = data.hold_token;
      this.holdAppointmentId = data.appointment_id;
      this.holdExpiresAt = new Date(data.expires_at.endsWith('Z') ? data.expires_at : data.expires_at + 'Z');
      this.startHoldTimer();
      return true;
    } catch {
      this.showModalError('Could not hold slot.');
      return false;
    }
  }

  async generateDeferredId() {
    const url = `${this.apiBase}/generate-id/`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        this.deferredAppointmentId = data.appointment_id;
      }
    } catch {
      // Best effort — email link won't work but order still processes
    }
  }

  async releaseHold() {
    if (!this.holdToken) return;
    const url = `${this.apiBase}/release/`;
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hold_token: this.holdToken }),
      });
    } catch {
      // Best effort
    }
    this.holdToken = null;
    this.holdAppointmentId = null;
    this.holdExpiresAt = null;
    if (this.holdTimer) {
      clearInterval(this.holdTimer);
      this.holdTimer = null;
    }
  }

  async loadUpdateMode(appointmentId) {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
      });
      const cart = await res.json();
      let item;
      if (appointmentId === 'deferred') {
        item = cart.items.find(i =>
          i.properties && i.properties._appointment_deferred === 'true'
        );
      } else {
        item = cart.items.find(i =>
          i.properties && i.properties._appointment_appointment_id === appointmentId
        );
      }
      if (!item) {
        this.render();
        return;
      }

      this.updateMode = true;
      this.updateAppointmentId = appointmentId;
      this.updateLineItemKey = item.key;

      const p = item.properties;
      this.holdToken = p._appointment_hold_token || null;

      // Restore forWhom / isGift from stored value
      const storedWhom = p._appointment_for_whom || 'self';
      if (storedWhom === 'gift') {
        this.forWhom = 'other';
        this.isGift = true;
      } else {
        this.forWhom = storedWhom;
        this.isGift = false;
      }

      this.deferred = p._appointment_deferred === 'true';
      this.recipientName = p._appointment_recipient_name || '';
      this.recipientEmail = p._appointment_recipient_email || '';
      this.giftMessage = p._appointment_gift_message || '';
      this.giftRevealDate = p._appointment_gift_reveal_date || '';
      if (this.giftRevealDate) {
        this.giftRevealTiming = 'later';
      }

      if (p._appointment_date) {
        this.selectedDate = p._appointment_date;
        const [y, m] = p._appointment_date.split('-');
        this.currentMonth = `${y}-${m}`;
      }

      // Release old hold before loading slots so the slot appears as available
      await this.releaseHold();

      this.render();

      // For update mode with an existing slot, open the modal and reload
      if (this.selectedDate && !this.deferred && this.effectiveForWhom !== 'gift') {
        this.openModal();
        await this.loadSlots(this.selectedDate);
        if (p._appointment_time) {
          const matchingSlot = this.slots.find(s => s.start_time === p._appointment_time);
          if (matchingSlot) {
            const held = await this.holdSlot(matchingSlot.slot_id);
            if (held) {
              this.selectedSlot = matchingSlot;
              this.renderSlots();
              this.updateModalConfirm();
              this.clearModalError();
            } else {
              this.showModalError('Your appointment hold has expired. Please choose another time.');
            }
          } else {
            this.showModalError('Your appointment hold has expired. Please choose another time.');
          }
        }
      }

      this.updateFormProperties();
      this.updateBuyButton();
    } catch {
      this.render();
    }
  }

  startHoldTimer() {
    if (this.holdTimer) clearInterval(this.holdTimer);
    this.renderHoldNotice();
    this.holdTimer = setInterval(() => {
      if (!this.holdExpiresAt) return;
      const remaining = this.holdExpiresAt - new Date();
      if (remaining <= 0) {
        clearInterval(this.holdTimer);
        this.holdTimer = null;
        this.holdToken = null;
        this.holdAppointmentId = null;
        this.holdExpiresAt = null;
        this.selectedSlot = null;
        this.showModalError('Your held slot has expired. Please select a new time.');
        if (this.selectedDate) this.loadSlots(this.selectedDate);
        this.updateModalConfirm();
        this.updateFormProperties();
        this.updateBuyButton();
        this.renderInlineSummary();
      } else {
        this.renderHoldNotice();
      }
    }, 1000);
  }

  // --- Rendering ---

  render() {
    const whom = this.effectiveForWhom;

    this.innerHTML = `
      <div class="appointment-picker">
        <div class="appointment-picker__radio-group">
          <label class="appointment-picker__radio-label">
            <input type="radio" name="appt-for-${this.formId}" value="self" ${this.forWhom === 'self' ? 'checked' : ''}>
            These jeans are for me
          </label>
          <label class="appointment-picker__radio-label">
            <input type="radio" name="appt-for-${this.formId}" value="other" ${this.forWhom === 'other' ? 'checked' : ''}>
            I'm buying these for someone else
          </label>
        </div>

        <div class="appointment-picker__scheduling-content">
          <div class="appointment-picker__other-section" style="display:${this.forWhom === 'other' ? 'block' : 'none'}">
            ${this.renderOtherFields()}
          </div>
          <div class="appointment-picker__schedule-section" data-whom="self" style="display:${this.effectiveForWhom === 'self' ? 'block' : 'none'}">
            ${this.renderSchedulingSelf()}
          </div>
          <div class="appointment-picker__schedule-section" data-whom="other" style="display:${this.effectiveForWhom === 'other' ? 'block' : 'none'}">
            ${this.renderSchedulingOther()}
          </div>
          <div class="appointment-picker__schedule-section" data-whom="gift" style="display:${this.effectiveForWhom === 'gift' ? 'block' : 'none'}">
            <p class="appointment-picker__schedule-prompt">We'll let them know they need to choose an appointment time when we tell them about your gift.</p>
          </div>

          <div id="appt-error"></div>
          <div id="appt-status"></div>
        </div>
      </div>

      <dialog class="appointment-picker__modal" id="appt-modal-${this.formId}">
        <div class="appointment-picker__modal-header">
          <span class="appointment-picker__modal-title">Schedule a measurement appointment</span>
          <button type="button" class="appointment-picker__modal-close">&times;</button>
        </div>
        <div class="appointment-picker__modal-body">
          <div class="appointment-picker__modal-calendar" id="appt-calendar"></div>
          <div class="appointment-picker__modal-slots" id="appt-slots">
            <div class="appointment-picker__slots-empty">Select a date to see available times</div>
          </div>
        </div>
        <div class="appointment-picker__modal-footer">
          <div id="appt-hold-notice"></div>
          <div class="appointment-picker__modal-actions">
            <button type="button" class="appointment-picker__modal-cancel" id="appt-cancel" style="display:none">Cancel appointment</button>
            <button type="button" class="appointment-picker__modal-confirm" id="appt-confirm" disabled>Confirm</button>
          </div>
        </div>
      </dialog>
    `;

    this.bindEvents();
    this.renderInlineSummary();
    this.updateFormProperties();
    this.updateBuyButton();
  }

  renderOtherFields() {
    return `
      <div class="appointment-picker__other-fields">
        <div class="appointment-picker__fields">
          <div class="appointment-picker__field">
            <label for="appt-recipient-name-${this.formId}">Their name</label>
            <input type="text" id="appt-recipient-name-${this.formId}" value="${this.escapeHtml(this.recipientName)}">
          </div>
          <div class="appointment-picker__field">
            <label for="appt-recipient-email-${this.formId}">Their email address</label>
            <input type="email" id="appt-recipient-email-${this.formId}" value="${this.escapeHtml(this.recipientEmail)}">
            <p class="appointment-picker__field-error" data-error-for="email">Please enter a valid email address</p>
          </div>
        </div>
        <div class="appointment-picker__gift-toggle">
          <span class="appointment-picker__gift-toggle-label">Are these jeans a gift?</span>
          <div class="appointment-picker__radio-group appointment-picker__radio-group--inline">
            <label class="appointment-picker__radio-label">
              <input type="radio" name="appt-gift-${this.formId}" value="no" ${!this.isGift ? 'checked' : ''}>
              No
            </label>
            <label class="appointment-picker__radio-label">
              <input type="radio" name="appt-gift-${this.formId}" value="yes" ${this.isGift ? 'checked' : ''}>
              Yes
            </label>
          </div>
        </div>
        <div class="appointment-picker__gift-section" style="display:${this.isGift ? 'block' : 'none'}">
          ${this.renderGiftFields()}
        </div>
      </div>
    `;
  }

  renderGiftFields() {
    return `
      <div class="appointment-picker__gift-fields">
        <div class="appointment-picker__fields">
          <div class="appointment-picker__field">
            <label for="appt-gift-message-${this.formId}">Gift message</label>
            <textarea id="appt-gift-message-${this.formId}">${this.escapeHtml(this.giftMessage)}</textarea>
          </div>
        </div>
        <div class="appointment-picker__reveal-timing">
          <span class="appointment-picker__reveal-timing-label">When should we tell them?</span>
          <div class="appointment-picker__radio-group appointment-picker__radio-group--inline">
            <label class="appointment-picker__radio-label">
              <input type="radio" name="appt-reveal-${this.formId}" value="now" ${this.giftRevealTiming === 'now' ? 'checked' : ''}>
              Now
            </label>
            <label class="appointment-picker__radio-label">
              <input type="radio" name="appt-reveal-${this.formId}" value="later" ${this.giftRevealTiming === 'later' ? 'checked' : ''}>
              Later (pick a date)
            </label>
          </div>
          <div class="appointment-picker__reveal-date-input" style="display:${this.giftRevealTiming === 'later' ? 'block' : 'none'}">
            <input type="date" id="appt-reveal-date-${this.formId}" value="${this.giftRevealDate}">
          </div>
        </div>
      </div>
    `;
  }

  renderSchedulingSelf() {
    return `
      <div class="appointment-picker__schedule-self">
        <p class="appointment-picker__schedule-prompt">You'll need to visit our studio to be measured.</p>
        <button type="button" class="appointment-picker__open-modal ${this.deferred ? 'is-disabled' : ''}" data-open-modal ${this.deferred ? 'disabled' : ''}>Schedule a measurement appointment now</button>
        <div class="appointment-picker__inline-confirm" hidden></div>
        <div class="appointment-picker__defer-option">
          <label class="appointment-picker__radio-label">
            <input type="checkbox" class="appt-defer-check" ${this.deferred ? 'checked' : ''}>
            Remind me later
          </label>
        </div>
        <div class="appointment-picker__defer-notice" hidden></div>
      </div>
    `;
  }

  renderSchedulingOther() {
    const name = this.recipientName.trim() || 'They';
    return `
      <div class="appointment-picker__schedule-other">
        <p class="appointment-picker__schedule-prompt">${this.escapeHtml(name)} will need to visit our studio to be measured.</p>
        <div class="appointment-picker__defer-option">
          <label class="appointment-picker__radio-label">
            <input type="checkbox" class="appt-defer-check" ${this.deferred ? 'checked' : ''}>
            Send them an email so they can schedule it
          </label>
        </div>
        <div class="appointment-picker__defer-notice" hidden></div>
        <button type="button" class="appointment-picker__open-modal ${this.deferred ? 'is-disabled' : ''}" data-open-modal ${this.deferred ? 'disabled' : ''}>Schedule a measurement appointment for them now</button>
        <div class="appointment-picker__inline-confirm" hidden></div>
      </div>
    `;
  }

  renderInlineSummary() {
    const confirmed = this.selectedSlot && this.holdToken;
    // In each schedule block, replace the "Schedule…" button with the confirmation
    // (held time + Change) when a slot is held; restore the button otherwise.
    this.querySelectorAll('.appointment-picker__schedule-section').forEach(section => {
      const slot = section.querySelector('.appointment-picker__inline-confirm');
      const btn = section.querySelector('[data-open-modal]');
      if (!slot) return; // gift block has no button/slot
      if (confirmed) {
        slot.innerHTML = `
          <div class="appointment-picker__summary">
            <span class="appointment-picker__summary-text">${this.formatTime(this.selectedSlot.start_time)} · ${this.formatDateDisplay(this.selectedDate)}</span>
            <button type="button" class="appointment-picker__change-link" data-appt-change>Change</button>
          </div>`;
        slot.hidden = false;
        // Inline style, not [hidden]: the button's CSS `display: inline-block`
        // overrides the hidden attribute, so it would otherwise stay visible.
        if (btn) btn.style.display = 'none';
      } else {
        slot.innerHTML = '';
        slot.hidden = true;
        if (btn) btn.style.display = '';
      }
    });
    this.querySelectorAll('[data-appt-change]').forEach(b => {
      b.addEventListener('click', () => this.openModal());
    });
  }

  showDeferNotice(msg) {
    this.querySelectorAll('.appointment-picker__defer-notice').forEach(el => {
      el.textContent = msg || '';
      el.hidden = !msg;
    });
  }

  bindEvents() {
    // For-whom radio — toggle visibility without full re-render
    this.querySelectorAll(`input[name="appt-for-${this.formId}"]`).forEach(radio => {
      radio.addEventListener('change', async () => {
        this.forWhom = radio.value;
        if (this.forWhom === 'self') {
          this.isGift = false;
        }
        // Clear deferred state — the hold is person-agnostic so keep it
        if (this.deferred) {
          this.deferred = false;
          this.deferredAppointmentId = null;
          this.querySelectorAll('.appt-defer-check').forEach(c => { c.checked = false; });
          this.querySelectorAll('[data-open-modal]').forEach(b => {
            b.disabled = false;
            b.classList.remove('is-disabled');
          });
        }
        const whom = this.effectiveForWhom;
        // Toggle other-fields section
        const otherSection = this.querySelector('.appointment-picker__other-section');
        if (otherSection) otherSection.style.display = this.forWhom === 'other' ? 'block' : 'none';
        // Toggle scheduling sections
        this.querySelectorAll('.appointment-picker__schedule-section').forEach(el => {
          el.style.display = el.dataset.whom === whom ? 'block' : 'none';
        });
        this.updateFormProperties();
        this.updateBuyButton();
      });
    });

    // Recipient fields
    const nameInput = this.querySelector(`#appt-recipient-name-${this.formId}`);
    const emailInput = this.querySelector(`#appt-recipient-email-${this.formId}`);
    if (nameInput) nameInput.addEventListener('input', (e) => {
      this.recipientName = e.target.value;
      this.updateSchedulePromptName();
      this.updateFormProperties();
      this.updateBuyButton();
    });
    if (emailInput) emailInput.addEventListener('input', (e) => {
      this.recipientEmail = e.target.value;
      if (this._emailTouched) this.updateEmailFeedback();
      this.updateFormProperties();
      this.updateBuyButton();
    });
    if (emailInput) emailInput.addEventListener('blur', () => {
      this._emailTouched = true;
      this.updateEmailFeedback();
    });

    // Gift toggle — toggle visibility without re-render
    this.querySelectorAll(`input[name="appt-gift-${this.formId}"]`).forEach(radio => {
      radio.addEventListener('change', () => {
        this.isGift = radio.value === 'yes';
        const giftSection = this.querySelector('.appointment-picker__gift-section');
        if (this.isGift) {
          this.deferred = true;
          this.selectedSlot = null;
          this.selectedDate = null;
          this.releaseHold();
          this.generateDeferredId();
          if (giftSection) giftSection.style.display = 'block';
        } else {
          this.deferred = false;
          this.deferredAppointmentId = null;
          if (giftSection) giftSection.style.display = 'none';
        }
        this.querySelectorAll('.appointment-picker__schedule-section').forEach(el => {
          el.style.display = el.dataset.whom === this.effectiveForWhom ? 'block' : 'none';
        });
        this.renderInlineSummary();
        this.updateFormProperties();
        this.updateBuyButton();
      });
    });

    // Gift message
    const msgInput = this.querySelector(`#appt-gift-message-${this.formId}`);
    if (msgInput) msgInput.addEventListener('input', (e) => {
      this.giftMessage = e.target.value;
      this.updateFormProperties();
    });

    // Reveal timing — toggle date input visibility without re-render
    this.querySelectorAll(`input[name="appt-reveal-${this.formId}"]`).forEach(radio => {
      radio.addEventListener('change', () => {
        this.giftRevealTiming = radio.value;
        const dateInput = this.querySelector('.appointment-picker__reveal-date-input');
        if (this.giftRevealTiming === 'now') {
          this.giftRevealDate = '';
          if (dateInput) dateInput.style.display = 'none';
        } else {
          if (dateInput) dateInput.style.display = 'block';
        }
        this.updateFormProperties();
        this.updateBuyButton();
      });
    });

    // Reveal date
    const revealInput = this.querySelector(`#appt-reveal-date-${this.formId}`);
    if (revealInput) revealInput.addEventListener('input', (e) => {
      this.giftRevealDate = e.target.value;
      this.updateFormProperties();
      this.updateBuyButton();
    });

    // Open modal — bind all instances
    this.querySelectorAll('[data-open-modal]').forEach(btn => {
      btn.addEventListener('click', () => this.openModal());
    });

    // Defer checkboxes — bind all instances, keep them in sync
    this.querySelectorAll('.appt-defer-check').forEach(check => {
      check.addEventListener('change', async () => {
        // Sync all defer checkboxes
        this.querySelectorAll('.appt-defer-check').forEach(c => { c.checked = check.checked; });
        if (check.checked) {
          const hadHold = !!this.holdToken;
          this.deferred = true;
          this.selectedSlot = null;
          this.selectedDate = null;
          await this.releaseHold();
          await this.generateDeferredId();
          this.renderInlineSummary();
          this.querySelectorAll('[data-open-modal]').forEach(b => {
            b.disabled = true;
            b.classList.add('is-disabled');
          });
          this.showDeferNotice(hadHold ? "We've released your appointment time — we'll follow up to schedule later" : '');
        } else {
          this.deferred = false;
          this.deferredAppointmentId = null;
          this.querySelectorAll('[data-open-modal]').forEach(b => {
            b.disabled = false;
            b.classList.remove('is-disabled');
          });
          this.showDeferNotice('');
        }
        this.updateFormProperties();
        this.updateBuyButton();
      });
    });

    // Modal close button
    const closeBtn = this.querySelector('.appointment-picker__modal-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeModal());

    // Modal confirm button
    const confirmBtn = this.querySelector('#appt-confirm');
    if (confirmBtn) confirmBtn.addEventListener('click', () => this.confirmModal());

    // Modal cancel button — release any held slot and back out gracefully
    const cancelBtn = this.querySelector('#appt-cancel');
    if (cancelBtn) cancelBtn.addEventListener('click', () => this.cancelAppointment());

    // Close modal on backdrop click
    const dialog = this.querySelector(`#appt-modal-${this.formId}`);
    if (dialog) {
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) this.closeModal();
      });
      // Fires on every close path (Esc, ✕, backdrop, confirm, cancel) — restore scroll.
      dialog.addEventListener('close', () => this.unlockScroll());
    }

    // Update mode: intercept form submit to swap cart line items
    if (this.updateMode) {
      const form = document.getElementById(this.formId);
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.submitUpdate(form);
        }, { capture: true, once: true });
      }
    }
  }

  updateSchedulePromptName() {
    const prompt = this.querySelector('.appointment-picker__schedule-other .appointment-picker__schedule-prompt');
    if (!prompt) return;
    const name = this.recipientName.trim() || 'They';
    prompt.textContent = `${name} will need to visit our studio to be measured.`;
  }

  updateEmailFeedback() {
    const errEl = this.querySelector('[data-error-for="email"]');
    if (!errEl) return;
    const email = this.recipientEmail.trim();
    const isValid = email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    errEl.style.visibility = isValid ? 'hidden' : 'visible';
  }

  // --- Modal ---

  openModal() {
    const dialog = this.querySelector(`#appt-modal-${this.formId}`);
    if (!dialog) return;

    // Uncheck defer when opening modal
    this.deferred = false;
    this.deferredAppointmentId = null;
    this.querySelectorAll('.appt-defer-check').forEach(c => { c.checked = false; });

    // Fresh start unless we're changing an already-held slot: drop any stale
    // date/slot so the user is prompted to pick a day again.
    if (!this.holdToken) {
      this.selectedDate = null;
      this.selectedSlot = null;
      this.slots = [];
    }

    this.lockScroll();
    dialog.showModal();
    this.loadAvailableDates();
    this.updateModalConfirm();

    if (this.selectedDate) {
      this.loadSlots(this.selectedDate);
    } else {
      this.renderSlots();
    }
  }

  closeModal() {
    const dialog = this.querySelector(`#appt-modal-${this.formId}`);
    if (dialog) dialog.close();
  }

  // Pin the page under the dialog (position:fixed handles iOS, where overflow
  // alone doesn't) and restore the scroll position when it closes — otherwise
  // the background scrolls behind the modal and you land in the wrong spot.
  lockScroll() {
    this._scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.width = '100%';
  }

  unlockScroll() {
    if (this._scrollY == null) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._scrollY);
    this._scrollY = null;
  }

  confirmModal() {
    if (!this.selectedSlot || !this.holdToken) return;
    this.closeModal();
    this.renderInlineSummary();
    this.updateFormProperties();
    this.updateBuyButton();
  }

  async cancelAppointment() {
    await this.releaseHold();
    this.selectedSlot = null;
    this.selectedDate = null;
    this.slots = [];
    this.closeModal();
    this.renderSlots();
    this.updateModalConfirm();
    this.renderInlineSummary();
    this.updateFormProperties();
    this.updateBuyButton();
  }

  updateModalConfirm() {
    const held = this.selectedSlot && this.holdToken;
    const btn = this.querySelector('#appt-confirm');
    if (btn) btn.disabled = !held;
    // "Cancel appointment" only applies when a slot is actually held; the ✕
    // handles plain close. Hidden otherwise.
    const cancelBtn = this.querySelector('#appt-cancel');
    if (cancelBtn) cancelBtn.style.display = held ? '' : 'none';
  }

  showModalError(msg) {
    const container = this.querySelector('#appt-slots');
    if (!container) return;
    const existing = container.querySelector('.appointment-picker__error');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.className = 'appointment-picker__error';
    el.textContent = msg;
    container.appendChild(el);
  }

  clearModalError() {
    const container = this.querySelector('#appt-slots');
    if (!container) return;
    const existing = container.querySelector('.appointment-picker__error');
    if (existing) existing.remove();
  }

  // --- Calendar rendering (inside modal) ---

  renderCalendar() {
    const container = this.querySelector('#appt-calendar');
    if (!container) return;
    if (!this.currentMonth) return;

    const [year, month] = this.currentMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    const now = new Date();
    const minMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    let html = `
      <div class="appointment-picker__calendar-nav">
        <button type="button" id="appt-prev-month" ${this.currentMonth <= minMonth ? 'disabled' : ''}>&lsaquo;</button>
        <span class="appointment-picker__month-title">${monthNames[month - 1]} ${year}</span>
        <button type="button" id="appt-next-month">&rsaquo;</button>
      </div>
      <div class="appointment-picker__calendar-grid">
    `;

    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (const dh of dayHeaders) {
      html += `<div class="appointment-picker__day-header">${dh}</div>`;
    }

    for (let i = 0; i < startDow; i++) {
      html += `<div class="appointment-picker__day"></div>`;
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isAvailable = this.availableDates.has(dateStr);
      const isSelected = this.selectedDate === dateStr;
      const isToday = dateStr === todayStr;
      const classes = [
        'appointment-picker__day',
        isAvailable ? 'is-available' : '',
        isSelected ? 'is-selected' : '',
        isToday ? 'is-today' : '',
      ].filter(Boolean).join(' ');
      html += `<button type="button" class="${classes}" ${isAvailable ? `data-date="${dateStr}"` : 'disabled'}>${d}</button>`;
    }

    html += '</div>';
    container.innerHTML = html;

    // Bind calendar nav
    const prevBtn = container.querySelector('#appt-prev-month');
    const nextBtn = container.querySelector('#appt-next-month');
    if (prevBtn) prevBtn.addEventListener('click', () => this.changeMonth(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => this.changeMonth(1));

    // Bind date clicks
    container.querySelectorAll('.appointment-picker__day[data-date]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.selectedDate = btn.dataset.date;
        this.selectedSlot = null;
        this.renderCalendar();
        this.loadSlots(this.selectedDate);
        this.updateModalConfirm();
      });
    });
  }

  changeMonth(delta) {
    const [y, m] = this.currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    this.currentMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    this.loadAvailableDates();
  }

  // --- Slots rendering (inside modal) ---

  renderSlots() {
    const container = this.querySelector('#appt-slots');
    if (!container) return;

    if (!this.selectedDate || this.slots.length === 0) {
      container.innerHTML = this.selectedDate && this.slots.length === 0
        ? '<div class="appointment-picker__slots-empty">No available times for this date.</div>'
        : '<div class="appointment-picker__slots-empty">Select a date to see available times</div>';
      return;
    }

    let html = `<div class="appointment-picker__slots">
      <div class="appointment-picker__slots-heading">Available times for ${this.formatDateDisplay(this.selectedDate)}</div>
      <div class="appointment-picker__slot-list">`;

    for (const slot of this.slots) {
      const isSelected = this.selectedSlot && this.selectedSlot.slot_id === slot.slot_id;
      html += `<button type="button" class="appointment-picker__slot ${isSelected ? 'is-selected' : ''}" data-slot-id="${slot.slot_id}">${this.formatTime(slot.start_time)}</button>`;
    }

    html += '</div></div>';
    container.innerHTML = html;

    container.querySelectorAll('.appointment-picker__slot').forEach(btn => {
      btn.addEventListener('click', async () => {
        const slotId = btn.dataset.slotId;
        const slot = this.slots.find(s => s.slot_id === slotId);
        if (!slot) return;

        const held = await this.holdSlot(slotId);
        if (held) {
          this.selectedSlot = slot;
          this.renderSlots();
          this.updateModalConfirm();
          this.clearModalError();
        }
      });
    });
  }

  renderHoldNotice() {
    const container = this.querySelector('#appt-hold-notice');
    if (!container || !this.holdExpiresAt) {
      if (container) container.innerHTML = '';
      return;
    }
    const remaining = Math.max(0, Math.floor((this.holdExpiresAt - new Date()) / 1000));
    const min = Math.floor(remaining / 60);
    const sec = remaining % 60;
    container.innerHTML = `<span class="appointment-picker__hold-notice">Slot held for ${min}:${String(sec).padStart(2, '0')}</span>`;
  }

  // --- Form integration ---

  updateFormProperties() {
    const form = document.getElementById(this.formId);
    if (!form) return;

    // Remove existing appointment properties
    form.querySelectorAll('input[name^="properties[_appointment"]').forEach(el => el.remove());

    const whom = this.effectiveForWhom;

    // For gifts, always set deferred
    if (whom === 'gift') {
      this.deferred = true;
    }

    if (!this.isComplete && !this.deferred && !this.selectedSlot) return;

    const state = this.computePurchaseState();
    const props = {
      '_appointment_purchase_state': state,
      '_appointment_for_whom': whom,
    };

    if (this.selectedSlot && this.holdToken) {
      props['_appointment_slot_id'] = this.selectedSlot.slot_id;
      props['_appointment_hold_token'] = this.holdToken;
      props['_appointment_appointment_id'] = this.holdAppointmentId;
      props['_appointment_date'] = this.selectedDate;
      props['_appointment_time'] = this.selectedSlot.start_time;
    }

    if (this.deferred) {
      props['_appointment_deferred'] = 'true';
      if (this.deferredAppointmentId) {
        props['_appointment_appointment_id'] = this.deferredAppointmentId;
      }
    }

    if (whom !== 'self') {
      if (this.recipientName.trim()) props['_appointment_recipient_name'] = this.recipientName.trim();
      if (this.recipientEmail.trim()) props['_appointment_recipient_email'] = this.recipientEmail.trim();
    }

    if (whom === 'gift') {
      if (this.giftMessage.trim()) props['_appointment_gift_message'] = this.giftMessage.trim();
      if (this.giftRevealDate) props['_appointment_gift_reveal_date'] = this.giftRevealDate;
    }

    for (const [key, value] of Object.entries(props)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      form.appendChild(input);
    }
  }

  updateBuyButton() {
    const form = document.getElementById(this.formId);
    if (!form) return;
    // Dawn's submit button is `.product-form__submit`; the venue theme used
    // `.js-product-add`. Match both so the gate finds the button either way.
    const btn = form.querySelector('.js-product-add, .product-form__submit');
    if (!btn) return;

    if (this.updateMode) {
      const textEl = btn.querySelector('.js-product-add-text, staged-action-text, span');
      const target = textEl || btn;
      target.textContent = 'Update appointment';

      if (!this._btnObserver) {
        this._btnObserver = new MutationObserver(() => {
          if (target.textContent !== 'Update appointment') {
            target.textContent = 'Update appointment';
          }
        });
        this._btnObserver.observe(target, { childList: true, characterData: true, subtree: true });
      }
    }

    btn.disabled = !this.isComplete;

    // Helper text below the button — kept in DOM and toggled via visibility so
    // its space is reserved when complete (the Add to Cart button doesn't jump).
    const formWrapper = btn.closest('.product-form__buttons') || btn.parentElement;
    let hint = formWrapper.querySelector('.appointment-picker__btn-hint');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'appointment-picker__btn-hint';
      formWrapper.insertBefore(hint, formWrapper.firstChild);
    }
    // Keep the last message while complete (hidden) so the button doesn't jump.
    const hintText = this.completionHint;
    if (hintText) hint.textContent = hintText;
    hint.style.visibility = this.isComplete ? 'hidden' : '';

    // Scroll-to-picker on disabled button tap/click.
    // Disabled buttons swallow click events, but pointerdown still fires.
    if (!this._btnClickBound) {
      this._btnClickBound = true;
      btn.addEventListener('pointerdown', (e) => {
        if (btn.disabled) {
          e.preventDefault();
          this.scrollToAndHighlight();
        }
      });
    }
  }

  scrollToAndHighlight() {
    // Highlight only the section that's actually incomplete (recipient details
    // vs. the appointment), falling back to the whole scheduling block.
    const target = this.incompleteSection() || this.querySelector('.appointment-picker__scheduling-content');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('appointment-picker--highlight');
    setTimeout(() => target.classList.remove('appointment-picker--highlight'), 3000);

    const form = document.getElementById(this.formId);
    if (!form) return;
    const hint = form.closest('.product-form__buttons')?.querySelector('.appointment-picker__btn-hint')
      || form.parentElement?.querySelector('.appointment-picker__btn-hint');
    if (hint) {
      hint.classList.add('appointment-picker__btn-hint--pulse');
      setTimeout(() => hint.classList.remove('appointment-picker__btn-hint--pulse'), 3000);
    }
  }

  async submitUpdate(form) {
    try {
      await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.updateLineItemKey, quantity: 0 }),
      });

      const data = new FormData(form);
      const entries = Object.fromEntries(data.entries());
      const properties = {};
      const item = {};
      for (const [key, value] of Object.entries(entries)) {
        const propMatch = key.match(/^properties\[(.+)\]$/);
        if (propMatch) {
          properties[propMatch[1]] = value;
        } else {
          item[key] = value;
        }
      }
      if (Object.keys(properties).length > 0) {
        item.properties = properties;
      }

      const addRes = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });

      if (!addRes.ok) {
        const err = await addRes.json();
        throw new Error(err.description || 'Could not update cart.');
      }

      // Fetch updated cart and notify the theme so the drawer refreshes
      const cartRes = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
        headers: { 'Accept': 'application/json' },
      });
      const cart = await cartRes.json();
      document.dispatchEvent(new CustomEvent('on:cart:loaded', {
        detail: { cart, type: 'ADD' },
      }));

      // Open the cart drawer
      const cartDrawer = document.querySelector('.modal--cart, .popup--cart-notification');
      if (cartDrawer && !cartDrawer.shown) {
        cartDrawer.open();
      }

      // Strip the ?appointment= param and reset to normal mode
      const url = new URL(window.location.href);
      url.searchParams.delete('appointment');
      window.history.replaceState({}, '', url.toString());
      this.updateMode = false;
      this.updateAppointmentId = null;
      this.updateLineItemKey = null;
      this.render();
    } catch (err) {
      this.showError(err.message || 'Could not update appointment.');
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.submitUpdate(form);
      }, { capture: true, once: true });
    }
  }

  // --- Helpers ---


  showError(msg) {
    const container = this.querySelector('#appt-error');
    if (container) container.innerHTML = `<div class="appointment-picker__error">${this.escapeHtml(msg)}</div>`;
  }

  clearError() {
    const container = this.querySelector('#appt-error');
    if (container) container.innerHTML = '';
  }

  formatTime(time) {
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:${m} ${ampm}`;
  }

  formatDateDisplay(dateStr) {
    const [y, m, d] = dateStr.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

if (!customElements.get('appointment-picker')) {
  customElements.define('appointment-picker', AppointmentPicker);
}
